import * as React from 'react';
import { Box, Container, Typography } from '@mui/material';
import StickyFooter from './components/StickyFooter';
import { companyName } from '../defaults/AppDefaults';
import backgroundImg from '../images/pexels-gromakova-92378617-9146943.jpg';

export default function TermsAndConditions() {
  return (
    <div style={{ backgroundColor: '#0f1013' }}>
      {/* Hero Header */}
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(8,9,12,0.82), rgba(8,9,12,0.9)), url(${backgroundImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#fff',
          textAlign: 'center',
          py: 10,
          px: 2,
          mt: 6,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            Terms and Conditions
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              maxWidth: 700,
              mx: 'auto',
            }}
          >
            Last updated: 31 July 2026
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container
        maxWidth="md"
        sx={{
          py: 8,
          px: { xs: 3, sm: 4 },
        }}
      >
        <Box sx={{ color: '#fff' }}>
          {/* Introduction */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            1. Agreement to Terms
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            Welcome to Barbers Limited. These Terms and Conditions ("Terms")
            govern your access to and use of our platform, services, and website
            (collectively, the "Services"). By accessing or using our Services,
            you agree to be bound by these Terms. If you do not agree to these
            Terms, you may not access or use our Services. Please read these
            Terms carefully before using our platform.
          </Typography>

          {/* Definitions */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            2. Definitions
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            For the purposes of these Terms:
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>
              <strong>"Platform"</strong> refers to the Barbers Limited website
              and mobile applications
            </li>
            <li>
              <strong>"User"</strong> refers to any person accessing or using
              our Services, including Customers, Barbers, and Shop Owners
            </li>
            <li>
              <strong>"Customer"</strong> refers to individuals who book
              barbering services through our Platform
            </li>
            <li>
              <strong>"Barber"</strong> refers to independent barbers who offer
              services through our Platform
            </li>
            <li>
              <strong>"Shop Owner"</strong> refers to barbershop owners who use
              our Platform to manage their business
            </li>
            <li>
              <strong>"Partner"</strong> refers collectively to Barbers and Shop
              Owners
            </li>
            <li>
              <strong>"Booking"</strong> refers to an appointment scheduled
              through our Platform
            </li>
          </Box>

          {/* Eligibility */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            3. Eligibility
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            You must be at least 18 years old to use our Services. By using our
            Services, you represent and warrant that you meet this age
            requirement and have the legal capacity to enter into these Terms.
            If you are using our Services on behalf of a business or
            organization, you represent that you have the authority to bind that
            entity to these Terms.
          </Typography>

          {/* Account Registration */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            4. Account Registration and Security
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            To access certain features of our Services, you must register for an
            account. When registering:
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>
              You must provide accurate, current, and complete information
            </li>
            <li>
              You must maintain and promptly update your account information
            </li>
            <li>
              You are responsible for maintaining the confidentiality of your
              account credentials
            </li>
            <li>
              You are responsible for all activities that occur under your
              account
            </li>
            <li>
              You must immediately notify us of any unauthorized use of your
              account
            </li>
            <li>You may not share your account credentials with others</li>
            <li>You may not create multiple accounts for deceptive purposes</li>
          </Box>

          {/* Use of Services */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            5. Use of Services
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            You agree to use our Services only for lawful purposes and in
            accordance with these Terms. You agree not to:
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe upon the rights of others</li>
            <li>Use the Services for fraudulent or deceptive purposes</li>
            <li>Interfere with or disrupt the Services or servers</li>
            <li>
              Attempt to gain unauthorized access to any part of the Services
            </li>
            <li>
              Use automated systems (bots, scripts) without our express
              permission
            </li>
            <li>Collect or harvest information about other users</li>
            <li>
              Post or transmit harmful, offensive, or inappropriate content
            </li>
            <li>Impersonate any person or entity</li>
          </Box>

          {/* Bookings and Appointments */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            6. Bookings and Appointments
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>For Customers:</strong>
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 3,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>
              Bookings are subject to availability and confirmation by the
              Barber or Shop Owner
            </li>
            <li>You must arrive on time for your appointment</li>
            <li>
              Cancellations or changes must be made in accordance with the
              service provider's cancellation policy
            </li>
            <li>Repeated no-shows may result in account restrictions</li>
            <li>
              Payment is due at the time of service unless otherwise agreed
            </li>
          </Box>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>For Barbers and Shop Owners:</strong>
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>
              You must honor confirmed bookings unless there is a valid reason
              for cancellation
            </li>
            <li>
              You must provide accurate availability and service information
            </li>
            <li>You are responsible for providing the services as described</li>
            <li>
              You must comply with all applicable health, safety, and licensing
              requirements
            </li>
            <li>You must set clear cancellation and refund policies</li>
          </Box>

          {/* Payments and Fees */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            7. Payments and Fees
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>Subscription Fees:</strong> Partners must pay the
            subscription fees associated with their chosen plan. Subscription
            fees are billed in advance on a recurring basis (monthly or annually
            as applicable). Fees are non-refundable except as required by law or
            as expressly stated in these Terms.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>Payment Processing:</strong> We use third-party payment
            processors (including Stripe) to handle transactions. By providing
            payment information, you authorize us and our payment processors to
            charge the applicable fees. You must provide accurate and complete
            payment information.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>Price Changes:</strong> We reserve the right to change our
            subscription fees at any time. We will provide you with reasonable
            notice of any fee changes. Continued use of the Services after a fee
            change constitutes acceptance of the new fees.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>Service Payments:</strong> Payments for barbering services
            are made directly between Customers and service providers. Barbers
            Limited is not a party to these transactions and is not responsible
            for payment disputes between Customers and Partners.
          </Typography>

          {/* Cancellations and Refunds */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            8. Cancellations and Refunds
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>Subscription Cancellations:</strong> Partners may cancel
            their subscription at any time through their account settings.
            Cancellations will take effect at the end of the current billing
            period. No refunds will be provided for partial billing periods.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>Appointment Cancellations:</strong> Cancellation policies
            for individual appointments are set by the Barber or Shop Owner.
            Customers should review the applicable cancellation policy before
            booking. Barbers Limited is not responsible for refunds related to
            service cancellations.
          </Typography>

          {/* Intellectual Property */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            9. Intellectual Property Rights
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>Our Content:</strong> All content on the Platform, including
            text, graphics, logos, images, software, and other materials
            (collectively, "Barbers Limited Content"), is owned by or licensed
            to Barbers Limited and is protected by copyright, trademark, and
            other intellectual property laws. You may not copy, reproduce,
            distribute, modify, or create derivative works of any Barbers
            Limited Content without our express written permission.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>User Content:</strong> You retain ownership of any content
            you post or upload to the Platform ("User Content"). By posting User
            Content, you grant Barbers Limited a worldwide, non-exclusive,
            royalty-free license to use, reproduce, modify, and display your
            User Content in connection with operating and promoting the
            Services. You represent that you own or have the necessary rights to
            all User Content you post.
          </Typography>

          {/* Prohibited Content */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            10. Prohibited Content
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            You may not post or upload User Content that:
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>
              Is illegal, harmful, threatening, abusive, harassing, defamatory,
              or obscene
            </li>
            <li>Infringes upon the intellectual property rights of others</li>
            <li>Contains viruses, malware, or other harmful code</li>
            <li>Violates the privacy or publicity rights of others</li>
            <li>Is false, misleading, or fraudulent</li>
            <li>Promotes discrimination, violence, or illegal activities</li>
            <li>Contains unsolicited advertising or spam</li>
          </Box>

          {/* Partner Obligations */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            11. Partner Obligations
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            If you are a Barber or Shop Owner, you agree to:
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>
              Maintain all necessary licenses, permits, and insurance required
              by law
            </li>
            <li>
              Comply with all applicable health, safety, and hygiene regulations
            </li>
            <li>
              Provide accurate descriptions of your services and qualifications
            </li>
            <li>
              Honor all bookings made through the Platform unless there is a
              valid reason for cancellation
            </li>
            <li>Treat all Customers with respect and professionalism</li>
            <li>Keep your availability and schedule up to date</li>
            <li>
              Not use the Platform to solicit Customers to book services outside
              the Platform
            </li>
            <li>
              Comply with your subscription plan limits (e.g., barber seat
              count, image uploads)
            </li>
          </Box>

          {/* Liability and Disclaimers */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            12. Disclaimers and Limitation of Liability
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>Services Provided "As Is":</strong> Our Services are
            provided on an "as is" and "as available" basis without warranties
            of any kind, either express or implied. We do not warrant that the
            Services will be uninterrupted, secure, or error-free.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>No Warranty for Service Quality:</strong> Barbers Limited is
            a platform that connects Customers with Barbers and Shop Owners. We
            do not provide barbering services directly and are not responsible
            for the quality, safety, or legality of services provided by
            Partners. We do not guarantee the accuracy of Partner information or
            the availability of appointments.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            <strong>Limitation of Liability:</strong> To the maximum extent
            permitted by law, Barbers Limited shall not be liable for any
            indirect, incidental, consequential, special, or punitive damages
            arising out of or related to your use of the Services. Our total
            liability to you for any claim arising from your use of the Services
            shall not exceed the amount you paid to us in the 12 months
            preceding the claim, or £100, whichever is greater.
          </Typography>

          {/* Indemnification */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            13. Indemnification
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            You agree to indemnify, defend, and hold harmless Barbers Limited,
            its affiliates, officers, directors, employees, and agents from and
            against any claims, liabilities, damages, losses, costs, or expenses
            (including reasonable legal fees) arising out of or related to: (a)
            your use of the Services; (b) your violation of these Terms; (c)
            your violation of any rights of another person or entity; or (d) any
            services you provide as a Partner.
          </Typography>

          {/* Termination */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            14. Termination
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            We reserve the right to suspend or terminate your account and access
            to the Services at any time, with or without notice, for any reason,
            including if we believe you have violated these Terms. Upon
            termination:
          </Typography>
          <Box
            component="ul"
            sx={{
              mb: 4,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <li>
              Your right to access and use the Services will immediately cease
            </li>
            <li>You will remain liable for any outstanding obligations</li>
            <li>
              We may delete your account data in accordance with our Privacy
              Policy
            </li>
            <li>
              Sections of these Terms that by their nature should survive
              termination will continue to apply
            </li>
          </Box>

          {/* Dispute Resolution */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            15. Dispute Resolution and Governing Law
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            These Terms shall be governed by and construed in accordance with
            the laws of England and Wales, without regard to conflict of law
            principles. Any disputes arising out of or related to these Terms or
            the Services shall be subject to the exclusive jurisdiction of the
            courts of England and Wales.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            Before initiating formal legal proceedings, we encourage you to
            contact us to seek an informal resolution of any dispute.
          </Typography>

          {/* Changes to Terms */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            16. Changes to Terms
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            We reserve the right to modify these Terms at any time. We will
            notify you of material changes by posting the updated Terms on the
            Platform and updating the "Last updated" date. Your continued use of
            the Services after changes are posted constitutes your acceptance of
            the modified Terms. We encourage you to review these Terms
            periodically.
          </Typography>

          {/* Third-Party Services */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            17. Third-Party Services and Links
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            Our Services may contain links to third-party websites or services
            that are not owned or controlled by Barbers Limited. We have no
            control over and assume no responsibility for the content, privacy
            policies, or practices of any third-party sites or services. We
            strongly advise you to read the terms and conditions and privacy
            policies of any third-party sites or services you visit.
          </Typography>

          {/* Entire Agreement */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            18. Entire Agreement
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            These Terms, together with our Privacy Policy and any other policies
            referenced herein, constitute the entire agreement between you and
            Barbers Limited regarding the Services and supersede all prior
            agreements and understandings, whether written or oral.
          </Typography>

          {/* Severability */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            19. Severability and Waiver
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            If any provision of these Terms is found to be invalid or
            unenforceable, that provision will be limited or eliminated to the
            minimum extent necessary, and the remaining provisions will remain
            in full force and effect. Our failure to enforce any right or
            provision of these Terms will not be deemed a waiver of such right
            or provision.
          </Typography>

          {/* Contact Information */}
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', mb: 2, color: '#dd8709' }}
          >
            20. Contact Us
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)' }}
          >
            If you have any questions or concerns about these Terms, please
            contact us at:
          </Typography>
          <Box
            sx={{
              mb: 6,
              pl: 4,
              color: 'rgba(255,255,255,0.85)',
              lineHeight: 1.8,
            }}
          >
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Barbers Limited</strong>
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Email: accounts@barbers.limited
            </Typography>
          </Box>
        </Box>
      </Container>

      <StickyFooter {...companyName} />
    </div>
  );
}
